import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";

export function PrivacyPage(): ReactNode {
  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="legal"
        title="Privacy Policy"
        emphasis="Policy"
        subhead="Last updated: 2026-05-16"
      />

      <div className="font-serif text-base text-inkSoft space-y-8 leading-relaxed">
        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">What we store</h2>
          <p>
            By default, Earwise stores all your data <strong className="text-ink">locally in your
            browser</strong> — card progress, review history, and preferences live in IndexedDB on
            your device. Nothing leaves your device unless you sign in.
          </p>
          <p className="mt-3">
            If you choose to sign in, we sync your card progress, review history, and preferences
            to our cloud database (Supabase, hosted on AWS). This is optional and only used for
            multi-device sync. We store your email address to identify your account.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">What we do not do</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>We do not sell your data.</li>
            <li>We do not run advertising.</li>
            <li>We do not use third-party analytics or tracking pixels.</li>
            <li>We do not collect usage statistics beyond what Supabase infrastructure logs for authenticated requests (standard web server logs, retained per Supabase&apos;s policy).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">Your rights</h2>
          <p>
            You can delete your account and all associated cloud data at any time from{" "}
            <Link to="/settings" className="underline hover:text-accent">
              Settings → Sync → Delete account
            </Link>
            . Deletion removes your data from our servers immediately. Your local browser
            data is not affected — clear it separately via{" "}
            <em>Settings → Data → Clear all data</em>.
          </p>
          <p className="mt-3">
            To request a data export or ask questions about your data, email us at{" "}
            <a href="mailto:calinlupa@gmail.com" className="underline hover:text-accent">
              calinlupa@gmail.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">Microphone</h2>
          <p>
            Vocal mode uses your microphone for pitch detection. Audio is processed entirely
            in your browser — it is never transmitted to any server. The browser will ask for
            permission before the microphone is accessed.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">Cookies</h2>
          <p>
            We do not use cookies. If you sign in, the Supabase SDK stores your session token
            in browser local storage, not in a cookie.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-medium text-ink mb-3">Changes</h2>
          <p>
            If we materially change this policy we will update the date above. Continued use of
            Earwise after a policy change means you accept the updated policy.
          </p>
        </section>
      </div>

      <footer className="mt-12 pt-6 border-t border-ink/10 flex gap-4">
        <Link to="/terms" className="text-sm text-inkFaded hover:text-ink underline">
          Terms of Service
        </Link>
        <Link to="/settings" className="text-sm text-inkFaded hover:text-ink underline">
          Settings
        </Link>
        <Link to="/" className="text-sm text-inkFaded hover:text-ink underline">
          Home
        </Link>
      </footer>
    </div>
  );
}
